import React, { useEffect, useState, useCallback } from "react";
import * as S from "./style";
import {
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Download,
} from "lucide-react";
import Table from "@components/table";
import { Reports, Zone } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { APP_BASE_URL, API_KEY } from "@utils/request";
import { toast } from "react-toastify";

const TransactionReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    completedAmount: 0,
    pendingAmount: 0,
    failedAmount: 0,
  });
  const { settings: currencySettings } = useGeneralSettings();
  const { bucketUrl } = useConfig();
  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: zones } = useSelector((state: RootState) => state.zones);

  useEffect(() => {
    Zone.list({ state: "zones" });
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    startDate: "",
    endDate: "",
    method: "",
    status: "",
    role: "",
    type: "",
    moduleId: "",
    zoneId: "",
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await Reports.getTransactionReport({
        params: {
          page,
          limit: 30,
          ...filters,
        },
      });
      if (res) {
        setData(res.data || []);
        setTotalItems(res.meta?.total || 0);
        setTotalPages(res.meta?.totalPages || 0);
        setSummary({
          totalAmount: res.summary?.totalAmount || 0,
          completedAmount: res.summary?.completedAmount || 0,
          pendingAmount: res.summary?.pendingAmount || 0,
          failedAmount: res.summary?.failedAmount || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === "all" ? "" : value }));
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    const toastId = toast.info("Generating Excel report, please wait...", { autoClose: false });
    try {
      const user = Helper.getUser();
      const token = user?.token;

      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.method) queryParams.append("method", filters.method);
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.type) queryParams.append("type", filters.type);
      if (filters.role) queryParams.append("role", filters.role);
      if (filters.moduleId) queryParams.append("moduleId", filters.moduleId);
      if (filters.zoneId) queryParams.append("zoneId", filters.zoneId);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);

      const downloadUrl = `${APP_BASE_URL}/admin/reports/transactions/export?${queryParams.toString()}`;

      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Api-Key": API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export Excel report");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `transaction_report_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.update(toastId, {
        render: "Excel report exported successfully!",
        type: "success",
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.update(toastId, {
        render: error?.message || "Failed to export Excel report",
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  const stats = [
    {
      label: "Total Transactions",
      value: Helper.formatCurrency(summary.totalAmount, currencySettings),
      Icon: DollarSign,
      color: "#1890ff",
      bgColor: "#e6f7ff",
    },
    {
      label: "Completed",
      value: Helper.formatCurrency(summary.completedAmount, currencySettings),
      Icon: CheckCircle,
      color: "#52c41a",
      bgColor: "#f6ffed",
    },
    {
      label: "Pending",
      value: Helper.formatCurrency(summary.pendingAmount, currencySettings),
      Icon: Clock,
      color: "#faad14",
      bgColor: "#fffbe6",
    },
    {
      label: "Failed",
      value: Helper.formatCurrency(summary.failedAmount, currencySettings),
      Icon: XCircle,
      color: "#ff4d4f",
      bgColor: "#fff1f0",
    },
  ];

  const columns = [
    { title: "Transaction ID", key: "id" },
    {
      title: "Date",
      key: "createdAt",
      render: (item: any) => new Date(item.createdAt).toLocaleDateString(),
    },
    {
      title: "User",
      key: "user",
      render: (item: any) => {
        const userName = item.user?.name || "Unknown";
        const role = item.user?.role?.toUpperCase() || "";
        const avatar = item.user?.avatar || item.user?.image;
        const initials = userName.charAt(0).toUpperCase();

        return (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <S.ProfileCard>
              {avatar ? (
                <S.ProfileAvatar
                  src={`${bucketUrl}/${avatar}`}
                  alt={userName}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling?.removeAttribute(
                      "style",
                    );
                  }}
                />
              ) : null}
              <S.ProfileAvatarPlaceholder
                variant={role === "STORE" ? "store" : "customer"}
                style={avatar ? { display: "none" } : undefined}
              >
                {initials}
              </S.ProfileAvatarPlaceholder>
              <S.ProfileDetails style={{ alignItems: "flex-start" }}>
                <S.ProfileName>{userName}</S.ProfileName>
                <S.ProfileSubtext>{role}</S.ProfileSubtext>
              </S.ProfileDetails>
            </S.ProfileCard>
          </div>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      render: (item: any) => (
        <span
          style={{
            color: item.type === "credit" ? "#10b981" : "#ef4444",
            fontWeight: 600,
          }}
        >
          {item.type === "credit" ? "+" : "-"}
          {Helper.formatCurrency(item.amount, currencySettings)}
        </span>
      ),
    },
    {
      title: "Type",
      key: "type",
      render: (item: any) => (
        <span style={{ fontSize: "12px", textTransform: "capitalize" }}>
          {item.type}
        </span>
      ),
    },
    {
      title: "Method",
      key: "method",
      render: (item: any) => (
        <span style={{ fontSize: "12px", textTransform: "uppercase" }}>
          {item.method}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (item: any) => {
        const isCompleted =
          item.status === "Success" || item.status === "Completed";
        const isPending = item.status === "Pending";
        return (
          <S.StatusPill
            bg={isCompleted ? "#f6ffed" : isPending ? "#fffbe6" : "#fff1f0"}
            color={isCompleted ? "#52c41a" : isPending ? "#faad14" : "#ff4d4f"}
          >
            {item.status}
          </S.StatusPill>
        );
      },
    },
  ];

  return (
    <S.Container>
      <S.Header>
        <S.Title>Transaction Report</S.Title>
        <S.SubTitle>
          Overview of all financial transactions across the platform.
        </S.SubTitle>
      </S.Header>

      <S.StatsGrid>
        {stats.map((stat, i) => (
          <S.StatCard key={i} color={stat.color}>
            <S.StatIcon color={stat.color} bgColor={stat.bgColor}>
              <stat.Icon size={24} />
            </S.StatIcon>
            <S.StatInfo>
              <S.StatValue>{stat.value}</S.StatValue>
              <S.StatLabel>{stat.label}</S.StatLabel>
            </S.StatInfo>
          </S.StatCard>
        ))}
      </S.StatsGrid>

      <S.FilterSection>
        <S.FilterItem>
          <S.Label>Search</S.Label>
          <S.Input
            type="text"
            placeholder="Search customer name..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Start Date</S.Label>
          <S.Input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange("startDate", e.target.value)}
          />
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>End Date</S.Label>
          <S.Input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange("endDate", e.target.value)}
          />
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Module</S.Label>
          <S.Select
            value={filters.moduleId || "all"}
            onChange={(e) => handleFilterChange("moduleId", e.target.value)}
          >
            <option value="all">All Modules</option>
            {modules?.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Zone</S.Label>
          <S.Select
            value={filters.zoneId || "all"}
            onChange={(e) => handleFilterChange("zoneId", e.target.value)}
          >
            <option value="all">All Zones</option>
            {zones?.map((z: any) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Role</S.Label>
          <S.Select
            value={filters.role || "all"}
            onChange={(e) => handleFilterChange("role", e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="customer">Customer</option>
            <option value="store">Store</option>
            <option value="delivery">Delivery</option>
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Type</S.Label>
          <S.Select
            value={filters.type || "all"}
            onChange={(e) => handleFilterChange("type", e.target.value)}
          >
            <option value="all">Both</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Payment Method</S.Label>
          <S.Select
            value={filters.method || "all"}
            onChange={(e) => handleFilterChange("method", e.target.value)}
          >
            <option value="all">All Methods</option>
            <option value="esewa">eSewa</option>
            <option value="khalti">Khalti</option>
            <option value="cod">COD</option>
            <option value="wallet">Wallet</option>
            <option value="manual">Manual/Admin</option>
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Status</S.Label>
          <S.Select
            value={filters.status || "all"}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </S.Select>
        </S.FilterItem>
      </S.FilterSection>

      <S.TableWrapper>
        <S.TableHeader>
          <S.TableTitle>All Transactions ({totalItems})</S.TableTitle>
          <S.ExportButton onClick={exportExcel} disabled={exporting}>
            <Download size={16} /> {exporting ? "Exporting..." : "Export Report"}
          </S.ExportButton>
        </S.TableHeader>
        <Table
          columns={[
            ...columns,
            {
              title: "Actions",
              key: "actions",
              render: (item: any) => (
                <button
                  onClick={() =>
                    navigate(`/admin/transaction/details/${item.id}`)
                  }
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: "#6366f1",
                    fontWeight: 500,
                  }}
                >
                  View
                </button>
              ),
            },
          ]}
          data={data}
          loading={loading}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={30}
          onPageChange={setPage}
          pageControlled={page}
        />
      </S.TableWrapper>
    </S.Container>
  );
};

export default TransactionReport;
